/**
 * Safe Zod v4 + react-hook-form resolver.
 *
 * `@hookform/resolvers/zod` v5 imports `zod/v4/core` and uses an `instanceof $ZodError`
 * check to decide whether a thrown error is a validation failure. When that check fails
 * (mismatched module instances across CJS/ESM, package boundaries, or duplicate installs)
 * the resolver rethrows the ZodError. RHF then propagates the rejection through
 * `handleSubmit`, leaving `formState.isSubmitting` stuck on `true` and the page with
 * an "Uncaught (in promise) ZodError" — no field-level error rendering ever fires.
 *
 * This resolver uses `safeParse` / `safeParseAsync` so nothing throws, and walks
 * `result.error.issues` into RHF's nested-error shape via `toNestErrors`.
 *
 * Use this in EVERY form across the monorepo instead of importing `zodResolver`
 * from `@hookform/resolvers/zod`. Build-time validator `validate:zod-resolver` blocks
 * direct imports of the upstream package.
 *
 * @see apps/hub/lib/utils/forms/zod-react-hook-form-resolver.ts (origin)
 * @see .claude/rules/main-FORM-VALIDATION-PATTERN.md
 */
import type {
  FieldError,
  FieldErrors,
  FieldValues,
  Resolver,
} from "react-hook-form";
import { toNestErrors, validateFieldsNatively } from "./hookform-resolver-utils";

type SafeZodResolverOptions = {
  /** @default 'async' */
  mode?: "sync" | "async";
  raw?: boolean;
};

type ZodIssueLike = {
  path: PropertyKey[];
  message: string;
  code: string;
};

type ZodSafeParseResult<TOutput> =
  | { success: true; data: TOutput }
  | { success: false; error: { issues: readonly ZodIssueLike[] } };

/** Structural schema type — avoids Zod 3 vs 4 `ZodType` generic mismatch across workspaces. */
type ZodSchemaLike<TOutput> = {
  safeParse(data: unknown): ZodSafeParseResult<TOutput>;
  safeParseAsync(data: unknown): Promise<ZodSafeParseResult<TOutput>>;
};

export function safeZodResolver<TFieldValues extends FieldValues>(
  schema: ZodSchemaLike<TFieldValues>,
  _schemaOptions?: unknown,
  resolverOptions: SafeZodResolverOptions = {},
): Resolver<TFieldValues> {
  return async (values, _context, options) => {
    const result =
      resolverOptions.mode === "sync"
        ? schema.safeParse(values)
        : await schema.safeParseAsync(values);

    if (result.success) {
      if (options.shouldUseNativeValidation) {
        validateFieldsNatively({}, options);
      }
      return {
        values: (resolverOptions.raw ? values : result.data) as TFieldValues,
        errors: {} as Record<string, never>,
      };
    }

    const flat: Record<string, FieldError> = {};
    for (const issue of result.error.issues) {
      const path = issue.path.map(String).join(".");
      const key = path.length > 0 ? path : "root";
      if (!flat[key]) {
        flat[key] = {
          message: issue.message,
          type: issue.code,
        };
      }
    }

    return {
      values: {} as Record<string, never>,
      errors: toNestErrors(flat, options) as FieldErrors<TFieldValues>,
    };
  };
}

/**
 * Drop-in replacement name matching the upstream API. Prefer this in new code so
 * a code search for `zodResolver` lands on the safe path.
 */
export { safeZodResolver as zodResolver };
